/**
 * Onboarding Checklist Resolver — pure classification logic.
 *
 * Composes signals from telemetry health, report readiness, configured capacity,
 * and topology to produce a per-property onboarding status with 13 checklist items.
 *
 * No side effects, no mutations. Exported for testing.
 */

// ─── Types ──────────────────────────────────────────────────────────────

export type OnboardingStatus =
  | 'not_started'
  | 'connected_no_data'
  | 'data_flowing'
  | 'needs_configuration'
  | 'needs_verification'
  | 'report_ready'
  | 'production_ready'
  | 'blocked';

export type ChecklistItemStatus = 'complete' | 'incomplete' | 'warning' | 'blocked' | 'not_applicable';

export interface ChecklistItem {
  key: string;
  label: string;
  status: ChecklistItemStatus;
  explanation: string;
  recommended_action: string | null;
  metadata: Record<string, unknown> | null;
}

export interface OnboardingChecklistInput {
  // Identity
  property_id: string;
  property_name: string;
  provider_type: string;
  org_name: string | null;
  system_count: number;

  // Telemetry health signals
  telemetry_status: 'healthy' | 'stale' | 'no_recent_data' | 'never_reported' | 'provider_error' | 'zero_data_only' | 'unknown';
  days_with_data_30: number;
  total_daily_summaries: number;
  last_poll_at: string | null;
  last_poll_status: string | null;
  consecutive_failures: number;

  // Capacity
  capacity_kwh: number | null;
  capacity_source: 'plant_configured' | 'property_total_fallback' | 'missing';
  capacity_confidence: 'verified' | 'needs_verification' | 'missing';
  possible_default_capacity: boolean;
  has_battery: boolean;

  // Battery snapshot
  has_battery_snapshot: boolean;
  last_snapshot_date: string | null;

  // Topology (multi-inverter only)
  is_multi_inverter: boolean;
  topology_confidence: string | null;
  topology_review_required: boolean;

  // Report readiness
  has_report_subscription: boolean;
  report_enabled: boolean;
  has_electricity_rate: boolean;
  report_days_with_data: number;
}

export interface OnboardingChecklistResult {
  overall_status: OnboardingStatus;
  completion_pct: number;
  items: ChecklistItem[];
}

// ─── Resolver ───────────────────────────────────────────────────────────

export function resolveOnboardingChecklist(input: OnboardingChecklistInput): OnboardingChecklistResult {
  const items = buildChecklist(input);
  const overall_status = classifyOverallStatus(input, items);
  const completion_pct = computeCompletionPct(items);

  return { overall_status, completion_pct, items };
}

// ─── Overall Status Classification ──────────────────────────────────────

function classifyOverallStatus(input: OnboardingChecklistInput, items: ChecklistItem[]): OnboardingStatus {
  const hasBlocked = items.some((i) => i.status === 'blocked');
  const hasWarning = items.some((i) => i.status === 'warning');
  const hasIncomplete = items.some((i) => i.status === 'incomplete');
  const applicableItems = items.filter((i) => i.status !== 'not_applicable');
  const completeItems = applicableItems.filter((i) => i.status === 'complete');

  // Blocked: provider error or persistent failure preventing progress
  if (input.telemetry_status === 'provider_error' || hasBlocked) {
    return 'blocked';
  }

  // Not started: never polled or no system registered
  if (input.telemetry_status === 'never_reported' && input.total_daily_summaries === 0 && !input.last_poll_at) {
    return 'not_started';
  }

  // Connected but no usable data yet
  if (input.telemetry_status === 'never_reported' || input.telemetry_status === 'zero_data_only') {
    return 'connected_no_data';
  }

  // All applicable items complete → production_ready
  if (completeItems.length === applicableItems.length) {
    return 'production_ready';
  }

  // Needs verification: has warnings about capacity or topology (takes priority over report_ready)
  if (hasWarning && !hasIncomplete) {
    return 'needs_verification';
  }

  // Report ready: telemetry healthy + report subscription active + no blockers + no warnings
  const reportItem = items.find((i) => i.key === 'report_subscription');
  const rateItem = items.find((i) => i.key === 'electricity_rate');
  const telItem = items.find((i) => i.key === 'telemetry_flowing');
  if (
    telItem?.status === 'complete' &&
    reportItem?.status === 'complete' &&
    rateItem?.status === 'complete' &&
    !hasBlocked &&
    !hasWarning
  ) {
    if (!hasIncomplete) return 'report_ready';
  }

  // Needs configuration: has data but missing config items
  if (input.days_with_data_30 >= 3 && hasIncomplete) {
    return 'needs_configuration';
  }

  // Data flowing: polling works, some data accumulating
  if (input.telemetry_status === 'healthy' || input.telemetry_status === 'stale' || input.days_with_data_30 > 0) {
    return 'data_flowing';
  }

  return 'connected_no_data';
}

// ─── Completion Percentage ──────────────────────────────────────────────

function computeCompletionPct(items: ChecklistItem[]): number {
  const applicable = items.filter((i) => i.status !== 'not_applicable');
  if (applicable.length === 0) return 100;
  const complete = applicable.filter((i) => i.status === 'complete').length;
  return Math.round((complete / applicable.length) * 100);
}

// ─── Checklist Items ────────────────────────────────────────────────────

function buildChecklist(input: OnboardingChecklistInput): ChecklistItem[] {
  return [
    checkSystemRegistered(input),
    checkProviderConnected(input),
    checkTelemetryFlowing(input),
    checkDataAccumulation(input),
    checkCapacityConfigured(input),
    checkCapacityVerified(input),
    checkBatterySnapshot(input),
    checkTopologyResolved(input),
    checkElectricityRate(input),
    checkReportSubscription(input),
    checkReportDataSufficiency(input),
    checkNoProviderErrors(input),
    checkProductionStable(input),
  ];
}

function checkSystemRegistered(input: OnboardingChecklistInput): ChecklistItem {
  const registered = input.system_count > 0;
  return {
    key: 'system_registered',
    label: 'System registered',
    status: registered ? 'complete' : 'incomplete',
    explanation: registered
      ? `${input.system_count} system(s) registered.`
      : 'No energy system registered for this property.',
    recommended_action: registered ? null : 'Add an energy system to this property.',
    metadata: { system_count: input.system_count },
  };
}

function checkProviderConnected(input: OnboardingChecklistInput): ChecklistItem {
  if (input.system_count === 0) {
    return {
      key: 'provider_connected',
      label: 'Provider connected',
      status: 'incomplete',
      explanation: 'No system to connect.',
      recommended_action: 'Register a system first.',
      metadata: null,
    };
  }

  const isError = input.telemetry_status === 'provider_error';
  const hasPolled = input.last_poll_at != null;

  if (isError) {
    return {
      key: 'provider_connected',
      label: 'Provider connected',
      status: 'blocked',
      explanation: `Provider error: ${input.last_poll_status ?? 'unknown'}`,
      recommended_action: 'Fix provider credentials or device registration.',
      metadata: { consecutive_failures: input.consecutive_failures, last_poll_status: input.last_poll_status },
    };
  }

  return {
    key: 'provider_connected',
    label: 'Provider connected',
    status: hasPolled ? 'complete' : 'incomplete',
    explanation: hasPolled ? `Connected via ${input.provider_type}.` : 'No poll attempts recorded.',
    recommended_action: hasPolled ? null : 'Verify provider credentials are saved.',
    metadata: { provider_type: input.provider_type },
  };
}

function checkTelemetryFlowing(input: OnboardingChecklistInput): ChecklistItem {
  const flowing = input.telemetry_status === 'healthy' || input.telemetry_status === 'stale';
  const isBlocked = input.telemetry_status === 'provider_error';

  if (isBlocked) {
    return {
      key: 'telemetry_flowing',
      label: 'Telemetry flowing',
      status: 'blocked',
      explanation: 'Provider error prevents telemetry collection.',
      recommended_action: 'Resolve provider connection first.',
      metadata: null,
    };
  }

  return {
    key: 'telemetry_flowing',
    label: 'Telemetry flowing',
    status: flowing ? 'complete' : 'incomplete',
    explanation: flowing
      ? `Receiving data (${input.days_with_data_30} days in last 30).`
      : input.telemetry_status === 'zero_data_only'
        ? 'Polling succeeds but all energy values are zero.'
        : 'No recent telemetry received.',
    recommended_action: flowing ? null : 'Check device is online and producing energy.',
    metadata: { telemetry_status: input.telemetry_status, days_with_data_30: input.days_with_data_30 },
  };
}

function checkDataAccumulation(input: OnboardingChecklistInput): ChecklistItem {
  const threshold = 7;
  const sufficient = input.days_with_data_30 >= threshold;

  return {
    key: 'data_accumulation',
    label: 'Sufficient data history',
    status: sufficient ? 'complete' : 'incomplete',
    explanation: sufficient
      ? `${input.days_with_data_30} days of data in last 30.`
      : `Only ${input.days_with_data_30} of ${threshold} required days.`,
    recommended_action: sufficient ? null : 'Wait for data to accumulate — system is polling.',
    metadata: { days_with_data_30: input.days_with_data_30, threshold },
  };
}

function checkCapacityConfigured(input: OnboardingChecklistInput): ChecklistItem {
  if (!input.has_battery) {
    return {
      key: 'capacity_configured',
      label: 'Battery capacity configured',
      status: 'not_applicable',
      explanation: 'No battery on this property.',
      recommended_action: null,
      metadata: null,
    };
  }

  const configured = input.capacity_source !== 'missing';

  return {
    key: 'capacity_configured',
    label: 'Battery capacity configured',
    status: configured ? 'complete' : 'incomplete',
    explanation: configured
      ? `${input.capacity_kwh} kWh (${input.capacity_source === 'plant_configured' ? 'plant-level' : 'property fallback'}).`
      : 'No battery capacity value set.',
    recommended_action: configured ? null : 'Set battery_kwh_configured on the plant or battery_kwh_total on the property.',
    metadata: { capacity_kwh: input.capacity_kwh, source: input.capacity_source },
  };
}

function checkCapacityVerified(input: OnboardingChecklistInput): ChecklistItem {
  if (!input.has_battery) {
    return {
      key: 'capacity_verified',
      label: 'Battery capacity verified',
      status: 'not_applicable',
      explanation: 'No battery on this property.',
      recommended_action: null,
      metadata: null,
    };
  }

  if (input.capacity_source === 'missing') {
    return {
      key: 'capacity_verified',
      label: 'Battery capacity verified',
      status: 'incomplete',
      explanation: 'Cannot verify — no capacity configured.',
      recommended_action: 'Configure capacity first.',
      metadata: null,
    };
  }

  // Suspicious default (30 kWh)
  if (input.possible_default_capacity) {
    return {
      key: 'capacity_verified',
      label: 'Battery capacity verified',
      status: 'warning',
      explanation: `${input.capacity_kwh} kWh may be a placeholder default — verify against actual hardware.`,
      recommended_action: 'Confirm capacity matches real battery spec sheet.',
      metadata: { capacity_kwh: input.capacity_kwh, possible_default: true },
    };
  }

  // Property fallback (not plant-level)
  if (input.capacity_confidence === 'needs_verification') {
    return {
      key: 'capacity_verified',
      label: 'Battery capacity verified',
      status: 'warning',
      explanation: `Using property-level fallback (${input.capacity_kwh} kWh). Not verified at plant level.`,
      recommended_action: 'Set battery_kwh_configured on the plant for verified capacity.',
      metadata: { capacity_kwh: input.capacity_kwh, source: input.capacity_source },
    };
  }

  return {
    key: 'capacity_verified',
    label: 'Battery capacity verified',
    status: 'complete',
    explanation: `${input.capacity_kwh} kWh verified at plant level.`,
    recommended_action: null,
    metadata: { capacity_kwh: input.capacity_kwh },
  };
}

function checkBatterySnapshot(input: OnboardingChecklistInput): ChecklistItem {
  if (!input.has_battery) {
    return {
      key: 'battery_snapshot',
      label: 'Battery health snapshot',
      status: 'not_applicable',
      explanation: 'No battery on this property.',
      recommended_action: null,
      metadata: null,
    };
  }

  return {
    key: 'battery_snapshot',
    label: 'Battery health snapshot',
    status: input.has_battery_snapshot ? 'complete' : 'incomplete',
    explanation: input.has_battery_snapshot
      ? `Latest snapshot: ${input.last_snapshot_date?.slice(0, 10) ?? 'unknown'}.`
      : 'No battery health snapshot computed yet.',
    recommended_action: input.has_battery_snapshot ? null : 'Will auto-compute once sufficient cycling data exists.',
    metadata: { last_snapshot_date: input.last_snapshot_date },
  };
}

function checkTopologyResolved(input: OnboardingChecklistInput): ChecklistItem {
  if (!input.is_multi_inverter) {
    return {
      key: 'topology_resolved',
      label: 'Battery topology resolved',
      status: 'not_applicable',
      explanation: 'Single-inverter system — topology not applicable.',
      recommended_action: null,
      metadata: null,
    };
  }

  if (input.topology_review_required) {
    return {
      key: 'topology_resolved',
      label: 'Battery topology resolved',
      status: 'warning',
      explanation: 'Multi-inverter topology requires manual review.',
      recommended_action: 'Review topology in Battery Topology tab and apply override if needed.',
      metadata: { confidence: input.topology_confidence },
    };
  }

  if (input.topology_confidence === 'low') {
    return {
      key: 'topology_resolved',
      label: 'Battery topology resolved',
      status: 'warning',
      explanation: 'Topology confidence is low — insufficient correlation data.',
      recommended_action: 'Wait for more cycling data or apply manual override.',
      metadata: { confidence: input.topology_confidence },
    };
  }

  return {
    key: 'topology_resolved',
    label: 'Battery topology resolved',
    status: 'complete',
    explanation: `Topology confidence: ${input.topology_confidence ?? 'N/A'}.`,
    recommended_action: null,
    metadata: { confidence: input.topology_confidence },
  };
}

function checkElectricityRate(input: OnboardingChecklistInput): ChecklistItem {
  return {
    key: 'electricity_rate',
    label: 'Electricity rate configured',
    status: input.has_electricity_rate ? 'complete' : 'incomplete',
    explanation: input.has_electricity_rate
      ? 'Electricity rate is set.'
      : 'No electricity rate — savings calculations will be unavailable.',
    recommended_action: input.has_electricity_rate ? null : 'Set electricity_rate_cents_per_kwh on the property.',
    metadata: null,
  };
}

function checkReportSubscription(input: OnboardingChecklistInput): ChecklistItem {
  if (!input.has_report_subscription) {
    return {
      key: 'report_subscription',
      label: 'Monthly report subscription',
      status: 'incomplete',
      explanation: 'No report subscription created.',
      recommended_action: 'Create a monthly report subscription for this property.',
      metadata: null,
    };
  }

  if (!input.report_enabled) {
    return {
      key: 'report_subscription',
      label: 'Monthly report subscription',
      status: 'warning',
      explanation: 'Subscription exists but is disabled.',
      recommended_action: 'Enable the report subscription.',
      metadata: { enabled: false },
    };
  }

  return {
    key: 'report_subscription',
    label: 'Monthly report subscription',
    status: 'complete',
    explanation: 'Active report subscription.',
    recommended_action: null,
    metadata: { enabled: true },
  };
}

function checkReportDataSufficiency(input: OnboardingChecklistInput): ChecklistItem {
  const threshold = 7;
  const sufficient = input.report_days_with_data >= threshold;

  return {
    key: 'report_data_sufficiency',
    label: 'Report data sufficiency',
    status: sufficient ? 'complete' : 'incomplete',
    explanation: sufficient
      ? `${input.report_days_with_data} days of reportable data.`
      : `Only ${input.report_days_with_data} of ${threshold} required days for reports.`,
    recommended_action: sufficient ? null : 'Wait for data to accumulate.',
    metadata: { days: input.report_days_with_data, threshold },
  };
}

function checkNoProviderErrors(input: OnboardingChecklistInput): ChecklistItem {
  const hasError = input.telemetry_status === 'provider_error' || input.consecutive_failures >= 5;

  return {
    key: 'no_provider_errors',
    label: 'No active provider errors',
    status: hasError ? 'blocked' : 'complete',
    explanation: hasError
      ? `${input.consecutive_failures} consecutive failures. Status: ${input.last_poll_status ?? 'unknown'}.`
      : 'No active provider errors.',
    recommended_action: hasError ? 'Fix provider credentials or device registration.' : null,
    metadata: hasError ? { consecutive_failures: input.consecutive_failures } : null,
  };
}

function checkProductionStable(input: OnboardingChecklistInput): ChecklistItem {
  // Production stable: healthy telemetry for 14+ days with no recent errors
  const stable = input.telemetry_status === 'healthy' &&
    input.days_with_data_30 >= 14 &&
    input.consecutive_failures === 0;

  if (input.telemetry_status === 'provider_error') {
    return {
      key: 'production_stable',
      label: 'Production stable (14+ days)',
      status: 'blocked',
      explanation: 'Cannot assess stability — provider error active.',
      recommended_action: 'Resolve provider error first.',
      metadata: null,
    };
  }

  return {
    key: 'production_stable',
    label: 'Production stable (14+ days)',
    status: stable ? 'complete' : 'incomplete',
    explanation: stable
      ? `Stable for ${input.days_with_data_30} days with zero failures.`
      : input.days_with_data_30 < 14
        ? `${input.days_with_data_30} of 14 required stable days.`
        : 'Recent instability detected.',
    recommended_action: stable ? null : 'Monitor — stability improves with time.',
    metadata: { days_with_data_30: input.days_with_data_30, consecutive_failures: input.consecutive_failures },
  };
}
